import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Icon,
  Flex,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  useDisclosure,
  useToast,
  MenuButton,
  Menu,
  MenuList,
  MenuItem,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogCloseButton,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { FaShoppingBag, FaTrash, FaEdit } from "react-icons/fa";
import { SwatchesPicker } from "react-color";
import RightHeader from "./RightHeader";
import axiosInstance from "../../helpers/axiosInstance";
import Translate from "../../components/LanguageTranslation/Translate";
import { textColor, categoryIcons } from "./BudgetPlanner";

const CategoryList = ({
  categories,
  setCategories,
  selectedTripId,
  selectedTrip,
  onOpenCategoryModal,
}) => {
  const [newCategoryName, setNewCategoryName] = useState("");
  const [categoryToEdit, setCategoryToEdit] = useState(null);
  const [selectedColor, setSelectedColor] = useState("#ff0000");
  const [selectedIcon, setSelectedIcon] = useState("Shopping");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  // AlertDialog state for delete confirmation
  const {
    isOpen: isAlertOpen,
    onOpen: onOpenAlert,
    onClose: onCloseAlert,
  } = useDisclosure();
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const cancelRef = React.useRef();

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axiosInstance.get(
          `/budget_planner/${selectedTripId}/categories`
        );
        console.log(response.data);
        setCategories(response.data);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    if (selectedTripId) {
      fetchCategories();
    }
  }, [selectedTripId]);

  const deleteCategory = async (categoryName) => {
    try {
      await axiosInstance.delete(
        `/budget_planner/${selectedTripId}/delete_category`,
        { data: { name: categoryName } }
      );
      const response = await axiosInstance.get(
        `/budget_planner/${selectedTripId}/categories`
      );
      setCategories(response.data);
    } catch (error) {
      console.error("Error deleting category:", error);
    }
  };

  // Handler to open the alert dialog
  const handleDeleteClick = (category) => {
    setCategoryToDelete(category);
    onOpenAlert();
  };

  // Handler for confirming deletion
  const handleConfirmDelete = async () => {
    if (categoryToDelete) {
      await deleteCategory(categoryToDelete.name);
      setCategoryToDelete(null);
      onCloseAlert();
    }
  };

  const openEditModal = (category) => {
    setCategoryToEdit(category);
    setSelectedColor(category.color ? category.color : "#000000");
    setSelectedIcon(category.icon ? category.icon : "Shopping");
    setNewCategoryName(category.name);
    onOpen();
  };

  const handleEditSubmit = async () => {
    if (!categoryToEdit) return;

    if (!newCategoryName) {
      toast({
        title: "Empty category",
        description: "Please fill in a name for the category.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      await axiosInstance.put(
        `/budget_planner/${selectedTripId}/update_category`,
        {
          old_name: categoryToEdit.name,
          new_name: newCategoryName,
          color: selectedColor,
          icon: selectedIcon,
        }
      );
      const response = await axiosInstance.get(
        `/budget_planner/${selectedTripId}/categories`
      );
      setCategories(response.data);
      onClose();
    } catch (error) {
      console.error("Error updating category:", error);
    }
  };

  return (
    <Box
      flex="3"
      w={{ base: "100%", md: "80%", lg: "75%" }}
      h={{ base: "auto", md: "100vh", lg: "100vh" }}
      mt={{ base: 4, md: 0, lg: 0 }}
      backgroundPosition="center"
      display="flex"
      flexDirection="column"
      boxLeftShadow="xl"
      borderRight="1px"
      borderColor="gray.200"
      overflowY={{ base: "none", md: "auto", lg: "auto" }}
      sx={{
        "&::-webkit-scrollbar": {
          display: "none",
        },
        msOverflowStyle: "none",
        scrollbarWidth: "none",
      }}
    >
      <RightHeader
        onOpenCategoryModal={onOpenCategoryModal}
        selectedTrip={selectedTrip}
      />
      <VStack
        align="start"
        spacing={3}
        p={4}
        w="100%"
        h="85vh"
        overflowY="auto"
        mt={3}
      >
        {categories.map((category, index) => (
          <Flex
            key={index}
            w="100%"
            justify="space-between"
            align="center"
            borderBottom="1px"
            borderColor="blackAlpha.100"
            pb={2}
          >
            <HStack spacing={3}>
              <Box
                w="40px"
                h="40px"
                bg={category.color || "#000000"}
                display="flex"
                alignItems="center"
                justifyContent="center"
                borderRadius="full"
              >
                <Icon
                  as={categoryIcons[category.icon]?.icon || FaShoppingBag}
                  color="white"
                  boxSize={5}
                />
              </Box>
              <Text fontSize="md" fontWeight="bold" ml={2} mb={0}>
                <Translate text={category.name} />
              </Text>
            </HStack>
            <HStack>
              {category.editable === true && (
                <>
                  <IconButton
                    icon={<FaEdit />}
                    bg="none"
                    color="black"
                    size="xs"
                    _hover="none"
                    onClick={() => openEditModal(category)}
                  />
                  <IconButton
                    icon={<FaTrash />}
                    bg="none"
                    color="black"
                    size="xs"
                    _hover="none"
                    onClick={() => handleDeleteClick(category)}
                  />
                </>
              )}
            </HStack>
          </Flex>
        ))}
      </VStack>

      <Modal isOpen={isOpen} onClose={onClose} size={{ base: "xs", md: "lg" }}>
        <ModalOverlay />
        <ModalContent p={4}>
          <ModalHeader>
            <Translate text="Edit Category" />
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Input
                placeholder="Enter new category name"
                bg="white"
                borderRadius="md"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
              />
              {/* <HStack spacing={8} align="center"> */}
              <Flex flexDirection={{ base: "column", md: "row" }} gap={8}>
                <Box>
                  <Menu>
                    <MenuButton
                      as={Button}
                      bg={selectedColor}
                      color={textColor(selectedColor)}
                      _hover={{ bg: selectedColor, opacity: 0.8 }}
                      _active={{ bg: selectedColor }}
                      _focus={{ bg: selectedColor, boxShadow: "outline" }}
                    >
                      <Translate text="Category Color" />
                    </MenuButton>
                    <MenuList p={2}>
                      <SwatchesPicker
                        color={selectedColor}
                        onChange={(color) => setSelectedColor(color.hex)}
                      />
                    </MenuList>
                  </Menu>
                </Box>

                <Flex align="center">
                  <Text fontSize="md" fontWeight="bold" ml={2} mr={2} mb={0}>
                    <Translate text="Icon:" />
                  </Text>
                  <Menu>
                    <MenuButton as={Button} rightIcon={<ChevronDownIcon />}>
                      <Box
                        w="40px"
                        h="40px"
                        bg={selectedColor}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        borderRadius="full"
                      >
                        <Icon
                          as={categoryIcons[selectedIcon]?.icon}
                          color="white"
                          boxSize={5}
                        />
                      </Box>
                    </MenuButton>
                    <MenuList
                      h={{ base: "40vh", md: "50vh" }}
                      overflowY="scroll"
                    >
                      {Object.keys(categoryIcons).map((category, index) => (
                        <MenuItem
                          key={index}
                          onClick={() => setSelectedIcon(category)}
                        >
                          <Box
                            w="40px"
                            h="40px"
                            bg={selectedColor}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            borderRadius="full"
                          >
                            <Icon
                              as={categoryIcons[category].icon}
                              color="white"
                              boxSize={5}
                            />
                          </Box>
                          <Text fontSize="md" fontWeight="bold" ml={2} mb={0}>
                            <Translate text={category} />
                          </Text>
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                </Flex>
              </Flex>
              {/* </HStack> */}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              // bg="#286328"
              bg="#286328"
              color="white"
              _hover="none"
              borderRadius="xl"
              w="full"
              onClick={handleEditSubmit}
            >
              <Translate text="Save" />
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* AlertDialog for delete confirmation */}
      <AlertDialog
        isOpen={isAlertOpen}
        leastDestructiveRef={cancelRef}
        onClose={onCloseAlert}
        size={{ base: "sm", md: "md" }}
        isCentered
      >
        <AlertDialogOverlay />
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            <Translate text="Delete Category" />
          </AlertDialogHeader>
          <AlertDialogCloseButton />
          <AlertDialogBody>
            <Translate text="Are you sure you want to delete this category? All associated expenses will also be permanently deleted. This action cannot be undone." />
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onCloseAlert}>
              <Translate text="Cancel" />
            </Button>
            <Button colorScheme="red" onClick={handleConfirmDelete} ml={3}>
              <Translate text="Delete" />
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Box>
  );
};

export default CategoryList;
